import { num, type ComponentForm } from '../component-types.js';

/**
 * Audio components (see engine/docs/systems/audio.md):
 * - `audioSource`: plays one clip, 2D or positioned in 3D, through a mixer bus.
 * - `audioListener`: the "ears" (usually on the camera; without one the game camera hears).
 */
const CLIP = ['.ogg', '.wav', '.mp3', '.flac'] as const;
const is3d = (v: Record<string, unknown>) => Number(v.spatialBlend ?? 0) > 0;

const audioSource: ComponentForm = {
  key: 'audioSource',
  label: 'Audio Source',
  icon: 'audio',
  order: 75,
  defaults: { clip: '', volume: 1, loop: false, playOnAwake: true, spatialBlend: 0 },
  fields: [
    { key: 'clip', label: 'Clip', type: { kind: 'path', suffix: CLIP }, hint: 'Sound file: .ogg, .wav, .mp3 or .flac.' },
    { key: 'output', label: 'Output Bus', type: { kind: 'string', placeholder: 'Master' }, hint: 'Bus of the mixer in settings.audio.mixer (empty = Master).' },
    { key: 'mute', label: 'Mute', type: { kind: 'bool' }, read: (v) => v.mute ?? false },
    { key: 'playOnAwake', label: 'Play On Awake', type: { kind: 'bool' }, hint: 'Starts when play mode starts or the entity spawns.', read: (v) => v.playOnAwake ?? true },
    { key: 'loop', label: 'Loop', type: { kind: 'bool' }, read: (v) => v.loop ?? false },
    { key: 'volume', label: 'Volume', type: num(0.05, { min: 0, max: 10 }), read: (v) => v.volume ?? 1 },
    { key: 'pitch', label: 'Pitch', type: num(0.05, { min: 0.01, max: 3 }), hint: 'Playback speed (1 = normal).', read: (v) => v.pitch ?? 1 },
    { key: 'startTime', label: 'Start Time (s)', type: num(0.1, { min: 0 }), read: (v) => v.startTime ?? 0 },
    {
      key: 'priority',
      label: 'Priority',
      type: num(1, { min: 0, max: 256, integer: true }),
      hint: '0 = most important. Above settings.audio.maxVoices the least important sounds stop.',
      read: (v) => v.priority ?? 128,
    },
    { key: 'panStereo', label: 'Stereo Pan', type: num(0.05, { min: -1, max: 1 }), hint: '-1 left .. 1 right (2D part).', read: (v) => v.panStereo ?? 0 },
    {
      key: 'spatialBlend',
      label: 'Spatial Blend',
      type: num(0.05, { min: 0, max: 1 }),
      hint: '0 = 2D (music, UI), 1 = 3D (volume and panning from the listener position).',
      read: (v) => v.spatialBlend ?? 0,
    },
    { key: 'minDistance', label: 'Min Distance', type: num(0.5, { min: 0.01 }), hint: 'Full volume within this many meters.', showIf: is3d, read: (v) => v.minDistance ?? 1 },
    { key: 'maxDistance', label: 'Max Distance', type: num(1, { min: 0.01 }), hint: 'Attenuation stops (logarithmic) or reaches silence (linear) here.', showIf: is3d, read: (v) => v.maxDistance ?? 500 },
    { key: 'rolloff', label: 'Rolloff', type: { kind: 'enum', options: ['logarithmic', 'linear', 'custom'] }, showIf: is3d, read: (v) => v.rolloff ?? 'logarithmic' },
    {
      key: 'rolloffCurve',
      label: 'Rolloff Curve',
      type: { kind: 'json' },
      hint: '[[distance / maxDistance, volume], ...], e.g. [[0, 1], [0.5, 0.3], [1, 0]].',
      showIf: (v) => is3d(v) && v.rolloff === 'custom',
    },
    { key: 'spread', label: 'Spread (deg)', type: num(5, { min: 0, max: 360 }), hint: '0 = point source, 360 = no panning.', showIf: is3d, read: (v) => v.spread ?? 0 },
    { key: 'dopplerLevel', label: 'Doppler Level', type: num(0.1, { min: 0, max: 5 }), hint: 'Pitch shift from relative motion (0 = off).', showIf: is3d, read: (v) => v.dopplerLevel ?? 1 },
    {
      key: 'ignoreListenerPause',
      label: 'Ignore Pause',
      type: { kind: 'bool' },
      hint: 'Keeps playing while the game is paused (time scale 0), e.g. pause-menu music.',
      read: (v) => v.ignoreListenerPause ?? false,
    },
  ],
};

const audioListener: ComponentForm = {
  key: 'audioListener',
  label: 'Audio Listener',
  icon: 'audio',
  order: 76,
  defaults: {},
  fields: [],
};

export default [audioSource, audioListener];
